import { Signal, signal } from '@angular/core';

import { removeFromSet } from '../set';
import { RequestWarning, RequestWarningType } from './RequestWarning';

export type RequestFailureMap<T> = Map<T, string | undefined>;

export interface RequestTrackerView<T> {
  readonly requestsInProgress: Signal<Set<T>>;

  /** Requests that were started. Not every search queries every backend - the vehicle search skips
   * ESO CRM outside GB and IE (CUSTAPP-607) - so the number of failures alone does not say whether
   * the search failed completely or only partially.
   */
  readonly attemptedRequests: Signal<Set<T>>;

  /** Requests that failed, i.e. were not completed successfully. This does not include requests that emitted warnings */
  readonly failedRequests: Signal<Set<T>>;
  readonly requestWarnings: Signal<Set<RequestWarningType>>;
}

export const blankRequestTrackerView = <T>(): RequestTrackerView<T> => ({
  requestsInProgress: signal(new Set<T>()).asReadonly(),
  attemptedRequests: signal(new Set<T>()).asReadonly(),
  failedRequests: signal(new Set<T>()).asReadonly(),
  requestWarnings: signal(new Set<RequestWarningType>()).asReadonly(),
});

export class RequestTracker<T> implements RequestTrackerView<T> {
  readonly requestsInProgress = signal(new Set<T>());
  readonly attemptedRequests = signal(new Set<T>());
  readonly failedRequests = signal(new Set<T>());
  readonly requestWarnings = signal(new Set<RequestWarningType>());

  async track<R>(type: T, handler: Promise<R>): Promise<R | undefined> {
    this.attemptedRequests.update((reqs) => new Set(reqs).add(type));
    this.requestsInProgress.update((reqs) => new Set(reqs).add(type));
    try {
      return await handler;
    } catch (error: unknown) {
      if (error instanceof RequestWarning) {
        this.requestWarnings.update((warnings) => new Set(warnings).add(error.type));
      } else {
        this.failedRequests.update((reqs) => new Set(reqs).add(type));
      }
      return undefined;
    } finally {
      this.requestsInProgress.update((reqs) => removeFromSet(reqs, type));
    }
  }

  asView(): RequestTrackerView<T> {
    return {
      attemptedRequests: this.attemptedRequests.asReadonly(),
      failedRequests: this.failedRequests.asReadonly(),
      requestsInProgress: this.requestsInProgress.asReadonly(),
      requestWarnings: this.requestWarnings.asReadonly(),
    };
  }

  static async track<T, R>(
    tracker: RequestTracker<T> | undefined,
    type: T,
    handler: Promise<R>,
  ): Promise<R | undefined> {
    if (tracker) return await tracker.track(type, handler);
    else return await handler;
  }
}
